import { useEffect, useState } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { dashboardApi, ApplicationsResponse, ApplicationsRow } from '../services/dashboardApi';

const NETWORK_LABELS: Record<string, string> = {
    'salus': 'Salus',
    'has-offers': 'Axo',
    'cake': 'Sortter',
};

const NETWORK_BADGE: Record<string, string> = {
    'salus': 'bg-teal-100 text-teal-800',
    'has-offers': 'bg-indigo-100 text-indigo-800',
    'cake': 'bg-amber-100 text-amber-800',
};

const GROUP_OPTIONS: { value: string; label: string }[] = [
    { value: 'partner', label: 'Partner' },
    { value: 'sub1', label: 'Liste / ESP (sub1)' },
    { value: 'sub3', label: 'Template (sub3)' },
    { value: 'score', label: 'Salus-score' },
    { value: 'country', label: 'Land' },
];

const SCORE_COLORS: Record<string, string> = {
    A: '#15803d', B: '#22c55e', C: '#84cc16', D: '#eab308', E: '#f97316', F: '#dc2626',
};

const fmt = (n: number | null | undefined, digits = 0) =>
    n === null || n === undefined ? '–' : Number(n).toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const pctClass = (pct: number | null) => {
    if (pct === null) return 'text-gray-400';
    if (pct >= 25) return 'text-green-700 font-semibold';
    if (pct >= 10) return 'text-green-600';
    if (pct >= 4) return 'text-amber-600';
    return 'text-red-600';
};

function ScoreMix({ row }: { row: ApplicationsRow }) {
    if (!row.scored) return <span className="text-gray-300">–</span>;
    const parts: [string, number][] = [
        ['A', row.score_a], ['B', row.score_b], ['C', row.score_c], ['D', row.score_d], ['E', row.score_e], ['F', row.score_f],
    ];
    return (
        <div className="flex h-3 w-32 rounded overflow-hidden bg-gray-100" title={parts.map(([s, n]) => `${s}: ${n}`).join('  ')}>
            {parts.filter(([, n]) => n > 0).map(([s, n]) => (
                <div key={s} style={{ width: `${(100 * n) / row.scored}%`, backgroundColor: SCORE_COLORS[s] }} />
            ))}
        </div>
    );
}

function Kpi({ title, value, sub }: { title: string; value: string; sub?: string }) {
    return (
        <div className="bg-white p-4 flex flex-col items-center justify-center">
            <div className="text-xs font-semibold text-gray-500 mb-1 text-center">{title}</div>
            <div className="text-2xl text-gray-800 font-display">{value}</div>
            {sub && <div className="text-[11px] text-gray-400 mt-1 text-center">{sub}</div>}
        </div>
    );
}

interface Props {
    days: number;
    geo: string;
    esp: string;
}

/**
 * Finance funnel: one row per loan application (admin.affiliate_applications).
 * Salus = all applications incl. scoreIq, Axo = received/offer/rejected/paid, Sortter = application -> paid.
 */
export default function ApplicationsPanel({ days, geo, esp }: Props) {
    const [groupBy, setGroupBy] = useState('partner');
    const [network, setNetwork] = useState('all');
    const [data, setData] = useState<ApplicationsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);
        setError('');
        dashboardApi.getApplications(days, groupBy, geo, esp, network)
            .then(setData)
            .catch((e: any) => setError(e.message || 'Kunne ikke hente ansøgninger'))
            .finally(() => setLoading(false));
    }, [days, groupBy, geo, esp, network]);

    const t = data?.totals;

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Ansøgninger (finansfunnel)</h3>
                        <p className="text-xs text-gray-500 max-w-3xl">
                            Én række pr. låneansøgning. <b>Salus</b>: alle ansøgninger inkl. score A–F ("afvist" = intet tilbud efter 3 dage).
                            {' '}<b>Axo</b>: modtaget → tilbud → afvist/betalt (T1–T5). <b>Sortter</b>: ansøgning (0 kr) → lån optaget; udløber efter 30 dage.
                            {' '}Opdateres hver time{data?.last_updated ? ` · senest ${new Date(data.last_updated).toLocaleString('da-DK')}` : ''}.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <select className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2"
                            value={network} onChange={(e) => setNetwork(e.target.value)}>
                            <option value="all">Alle netværk</option>
                            <option value="salus">Salus</option>
                            <option value="has-offers">Axo</option>
                            <option value="cake">Sortter</option>
                        </select>
                        <select className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2"
                            value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                            {GROUP_OPTIONS.map((o) => <option key={o.value} value={o.value}>Gruppér: {o.label}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {error && <div className="bg-red-50 text-red-700 p-3 rounded-xl border border-red-200 text-sm">{error}</div>}

            {/* KPIs */}
            <div className="rounded-sm shadow-sm border border-gray-200 bg-gray-200 gap-[1px] grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 overflow-hidden">
                <Kpi title="Ansøgninger" value={loading ? '…' : fmt(t?.applications)} />
                <Kpi title="Fik tilbud" value={loading ? '…' : fmt(t?.offers)}
                    sub={t?.applications ? `${fmt((100 * (t.offers || 0)) / t.applications, 1)} %` : undefined} />
                <Kpi title="Betalt" value={loading ? '…' : fmt(t?.paid)} />
                <Kpi title="Betalt %" value={loading ? '…' : (t?.paid_pct === null || t?.paid_pct === undefined ? '–' : `${fmt(t.paid_pct, 1)} %`)} />
                <Kpi title="Omsætning (DKK)" value={loading ? '…' : fmt(t?.revenue_dkk)} />
                <Kpi title="DKK pr. ansøgning" value={loading ? '…' : fmt(t?.dkk_per_application)} sub="forventet værdi" />
                <Kpi title="Median tid til betaling" value={loading ? '…' : (t?.median_hours_to_paid == null ? '–' : `${fmt(t.median_hours_to_paid, 1)} t`)} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Salus score -> outcome */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">Salus: score → udfald</h4>
                    <p className="text-[11px] text-gray-400 mb-3">Optimeringssignal: scoren kommer med det samme, betalingen senere.</p>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b">
                                <th className="py-1">Score</th>
                                <th className="py-1 text-right">Ansøgn.</th>
                                <th className="py-1 text-right">Betalt %</th>
                                <th className="py-1 text-right">DKK/ansøgn.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.salus_scores || []).map((s) => (
                                <tr key={s.score} className="border-b last:border-0">
                                    <td className="py-1.5">
                                        <span className="inline-block w-6 text-center rounded text-white text-xs font-bold"
                                            style={{ backgroundColor: SCORE_COLORS[s.score] || '#9ca3af' }}>{s.score}</span>
                                    </td>
                                    <td className="py-1.5 text-right">{fmt(s.applications)}</td>
                                    <td className={`py-1.5 text-right ${pctClass(s.paid_pct)}`}>{s.paid_pct == null ? '–' : `${fmt(s.paid_pct, 1)} %`}</td>
                                    <td className="py-1.5 text-right font-medium">{fmt(s.dkk_per_application)}</td>
                                </tr>
                            ))}
                            {!loading && (data?.salus_scores || []).length === 0 && (
                                <tr><td colSpan={4} className="py-3 text-center text-gray-400 text-xs">Ingen Salus-ansøgninger i filteret</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Daily */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 lg:col-span-2 h-[300px] flex flex-col">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Ansøgninger pr. dag (efter ansøgningsdato)</h4>
                    <div className="flex-grow">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={data?.daily || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false}
                                    tickFormatter={(v) => { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; }} />
                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <Tooltip />
                                <Legend verticalAlign="top" iconSize={10} wrapperStyle={{ fontSize: '11px' }} />
                                <Bar dataKey="applications" name="Ansøgninger" fill="#cbd5e1" />
                                <Bar dataKey="rejected" name="Afvist" fill="#fca5a5" />
                                <Line dataKey="paid" name="Betalt" stroke="#16a34a" strokeWidth={2} dot={false} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Grouped table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-sm whitespace-nowrap">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs text-gray-500 border-b">
                                <th className="px-4 py-2">{GROUP_OPTIONS.find((o) => o.value === groupBy)?.label}</th>
                                <th className="px-3 py-2 text-right">Ansøgn.</th>
                                <th className="px-3 py-2 text-right">Tilbud</th>
                                <th className="px-3 py-2 text-right">Betalt</th>
                                <th className="px-3 py-2 text-right">Afvist</th>
                                <th className="px-3 py-2 text-right">Åbne</th>
                                <th className="px-3 py-2 text-right">Betalt %</th>
                                <th className="px-3 py-2 text-right">DKK</th>
                                <th className="px-3 py-2 text-right">DKK/ansøgn.</th>
                                <th className="px-3 py-2 text-right">DKK/betalt</th>
                                <th className="px-3 py-2 text-right">Median t.</th>
                                <th className="px-3 py-2">Score-mix (A→F)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan={12} className="px-4 py-6 text-center text-gray-400">Henter…</td></tr>
                            ) : (data?.rows || []).map((r) => (
                                <tr key={r.key} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium text-gray-800 max-w-[340px] truncate" title={r.label}>
                                        {groupBy === 'partner' && r.network_id && (
                                            <span className={`mr-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${NETWORK_BADGE[r.network_id] || 'bg-gray-100 text-gray-700'}`}>
                                                {NETWORK_LABELS[r.network_id] || r.network_id}
                                            </span>
                                        )}
                                        {r.label}
                                    </td>
                                    <td className="px-3 py-2 text-right">{fmt(r.applications)}</td>
                                    <td className="px-3 py-2 text-right">{fmt(r.offers)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{fmt(r.paid)}</td>
                                    <td className="px-3 py-2 text-right text-gray-500">{fmt(r.rejected)}</td>
                                    <td className="px-3 py-2 text-right text-gray-500">{fmt(r.open)}</td>
                                    <td className={`px-3 py-2 text-right ${pctClass(r.paid_pct)}`}>{r.paid_pct == null ? '–' : `${fmt(r.paid_pct, 1)} %`}</td>
                                    <td className="px-3 py-2 text-right">{fmt(r.revenue_dkk)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{fmt(r.dkk_per_application)}</td>
                                    <td className="px-3 py-2 text-right text-gray-500">{fmt(r.dkk_per_paid)}</td>
                                    <td className="px-3 py-2 text-right text-gray-500">{r.median_hours_to_paid == null ? '–' : fmt(r.median_hours_to_paid, 1)}</td>
                                    <td className="px-3 py-2"><ScoreMix row={r} /></td>
                                </tr>
                            ))}
                            {!loading && (data?.rows || []).length === 0 && (
                                <tr><td colSpan={12} className="px-4 py-6 text-center text-gray-400">Ingen ansøgninger i filteret</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
